import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { apiPath } from "../utils/apiClient";

const AuthContext = createContext(null);
const TOKEN_KEY = "tts_auth_token";
const USER_KEY = "tts_auth_user";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(
    () => localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || null
  );
  const [loading, setLoading] = useState(true);

  const persistSession = useCallback((nextToken, nextUser, remember = true) => {
    const storage = remember ? localStorage : sessionStorage;
    const other = remember ? sessionStorage : localStorage;
    other.removeItem(TOKEN_KEY);
    other.removeItem(USER_KEY);
    storage.setItem(TOKEN_KEY, nextToken);
    storage.setItem(USER_KEY, JSON.stringify(nextUser));
    setToken(nextToken);
    setUser(nextUser);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.removeItem("tts_admin");
    sessionStorage.removeItem("tts_admin_details");
    sessionStorage.removeItem("tts_user");
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const activeToken = token || localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
      if (activeToken) {
        config.headers.Authorization = `Bearer ${activeToken}`;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, [token]);

  useEffect(() => {
    const bootstrap = async () => {
      const activeToken = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
      if (!activeToken) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await axios.get(apiPath("/api/auth/me"), {
          headers: { Authorization: `Bearer ${activeToken}` },
        });
        setUser(data.user);
        setToken(activeToken);
      } catch {
        clearSession();
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [clearSession]);

  const login = async (email, password, remember = true) => {
    const { data } = await axios.post(apiPath("/api/auth/login"), { email, password });
    persistSession(data.token, data.user, remember);
    return data.user;
  };

  const register = async (name, email, password, remember = true) => {
    const { data } = await axios.post(apiPath("/api/auth/register"), { name, email, password });
    persistSession(data.token, data.user, remember);
    return data.user;
  };

  const logout = () => clearSession();

  const isAdmin = user?.role === "admin";
  const isAuthenticated = Boolean(user && token);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        isAdmin,
        isAuthenticated,
        login,
        register,
        logout,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
